import { useEffect } from 'react';
import { useReducedMotion } from '../lib/useReducedMotion.js';

/**
 * SignatureCursor — the NATIVE mouse is left exactly as-is (no replacement, no
 * lag); this only sheds a fine trail of gold-leaf flakes behind the pointer.
 *
 * Flakes are emitted by DISTANCE travelled (one per ~STEP px), so the trail stays
 * even whether you drift or flick, and each flake drifts back along your motion
 * with a touch of gravity, spins, and fades — like real leaf flaking off and
 * settling. One recycled pool, no React state per frame. Desktop fine-pointer
 * only; disabled on touch / reduced-motion.
 */
const POOL = 18;
const STEP = 19; // px of travel between flakes
const SHAPES = 4; // matches the .s0–.s3 clip-paths in globals.css

export function SignatureCursor(): null {
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    if (typeof window === 'undefined' || !window.matchMedia('(pointer: fine)').matches) return;

    const root = document.createElement('div');
    root.className = 'sig-root';
    const flakes: HTMLElement[] = [];
    for (let i = 0; i < POOL; i += 1) {
      const f = document.createElement('div');
      f.className = `sig-fleck s${i % SHAPES}`;
      root.appendChild(f);
      flakes.push(f);
    }
    document.body.appendChild(root);

    let lastX: number | null = null;
    let lastY: number | null = null;
    let acc = 0;
    let fi = 0;

    const spawn = (x: number, y: number, ux: number, uy: number): void => {
      const f = flakes[fi % POOL];
      fi += 1;
      if (!f) return;
      const r = Math.random();
      const r2 = Math.random();
      const spread = 5 + r * 7;
      // Drift OPPOSITE the motion (trailing) + a little gravity + a touch of spread.
      const tx = -ux * spread + (r2 - 0.5) * 9;
      const ty = -uy * spread + 5 + r2 * 7;
      f.style.left = `${x}px`;
      f.style.top = `${y}px`;
      f.style.setProperty('--sz', `${(5.5 + r * 5).toFixed(1)}px`);
      f.style.setProperty('--tx', `${tx.toFixed(1)}px`);
      f.style.setProperty('--ty', `${ty.toFixed(1)}px`);
      f.style.setProperty('--rot', `${Math.floor(r * 360)}deg`);
      f.style.setProperty('--spin', `${((r2 - 0.5) * 90).toFixed(0)}deg`);
      f.style.setProperty('--op', `${(0.68 + r2 * 0.22).toFixed(2)}`);
      f.style.setProperty('--dur', `${Math.floor(560 + r * 260)}ms`);
      f.classList.remove('run');
      void f.offsetWidth; // restart the fade
      f.classList.add('run');
    };

    const onMove = (e: PointerEvent): void => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const x = e.clientX;
      const y = e.clientY;
      if (lastX === null || lastY === null) {
        lastX = x;
        lastY = y;
        return;
      }
      const dx = x - lastX;
      const dy = y - lastY;
      const dist = Math.hypot(dx, dy);
      lastX = x;
      lastY = y;
      if (dist === 0) return;
      const ux = dx / dist;
      const uy = dy / dist;
      acc += dist;
      let budget = 4; // cap flakes per event so a fast flick can't burst the pool
      while (acc >= STEP && budget > 0) {
        acc -= STEP;
        budget -= 1;
        spawn(x, y, ux, uy);
      }
      if (acc > STEP) acc = 0; // drop any backlog past the cap
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      root.remove();
    };
  }, [reduced]);

  return null;
}
